import { ChangeEvent, useState } from 'react';

import { Alert, Button, Col, Form, FormControl, Row } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { useUpdateEffect } from 'react-use';
import { DateTime } from 'luxon';

import { useAppSelector } from 'hooks';
import { InfoIcon } from 'assets/icons';
import { Dropdown } from 'components/Forms';
import { DatePicker, ModalV2 } from 'components/UI';
import { Error, Loading } from 'components/Fetch';
import { baselineClasses } from 'constants/constants';
import { getGroupTZ } from 'store/groups/reducer';
import {
  getBoundsForClass,
  getPrefUnitForMeasurementClass
} from 'utils/MeasurementsStoreFunctions';
import {
  PRESSURE_BASELINE_CLASS,
  formatBaselineValue,
  getBaselinePartClasses,
  getBaselineUnitClass,
  getBaselineUnitLabel
} from 'utils/baselines';

type IPartKey = 'main' | IBaselinePartKey;
type IField = 'low' | 'high';
type IPartInputs = Partial<Record<IPartKey, Record<IField, string>>>;

interface IAddEditBaselineProps {
  show: boolean;
  setModal: (show: boolean) => void;
  mode: IEditCarePlanMode;
  baseline?: IBaselineItem;
  ignoreClasses: string[];
  loading: boolean;
  error: boolean;
  onSave: (values: IBaselineFormValues) => void;
}

const getPartKeys = (className: string): IPartKey[] => {
  return getBaselinePartClasses(className).map((part) => {
    return part === className ? 'main' : (part as IBaselinePartKey);
  });
};

const getBoundsClass = (partKey: IPartKey, className: string) => {
  return partKey === 'main' ? className : partKey;
};

const getDefaultUnit = (className: string) => {
  return getPrefUnitForMeasurementClass(getBaselineUnitClass(className));
};

const toDateString = (date: Date | null) => (date ? DateTime.fromJSDate(date).toISODate() : '');

const AddEditBaseline = ({
  show,
  setModal,
  mode,
  baseline,
  ignoreClasses,
  loading,
  error,
  onSave
}: IAddEditBaselineProps) => {
  const intl = useIntl();
  const groupTZ = useAppSelector(getGroupTZ);
  const measurementClasses = useAppSelector((state) => state.settings.citc?.measurementClasses);
  const groupToday = DateTime.now().setZone(groupTZ);
  const maxDate = DateTime.local(groupToday.year, groupToday.month, groupToday.day).toJSDate();

  const getInitialParts = (className: string, unit: string): IPartInputs =>
    getPartKeys(className).reduce<IPartInputs>((acc, partKey) => {
      const range = partKey === 'main' ? baseline : baseline?.[partKey];
      const boundsClass = getBoundsClass(partKey, className);
      const format = (value?: number | null) => {
        return value === undefined || value === null
          ? ''
          : formatBaselineValue(value, boundsClass, unit);
      };
      acc[partKey] = { low: format(range?.low), high: format(range?.high) };
      return acc;
    }, {});

  const getInitialDate = () => {
    return baseline?.effectiveDate ? DateTime.fromISO(baseline.effectiveDate).toJSDate() : maxDate;
  };

  const initialClass = baseline?.class || '';
  const initialUnit = baseline?.unit || (initialClass ? getDefaultUnit(initialClass) : '');
  const [className, setClassName] = useState(initialClass);
  const [unit, setUnit] = useState(initialUnit);
  const [initialParts, setInitialParts] = useState(getInitialParts(initialClass, initialUnit));
  const [parts, setParts] = useState(initialParts);
  const [effectiveDate, setEffectiveDate] = useState<Date | null>(getInitialDate());

  useUpdateEffect(() => {
    if (!show) return;
    const newInitialParts = getInitialParts(initialClass, initialUnit);
    setClassName(initialClass);
    setUnit(initialUnit);
    setInitialParts(newInitialParts);
    setParts(newInitialParts);
    setEffectiveDate(getInitialDate());
  }, [show, baseline]);

  const classOptions: ISelectOption[] = baselineClasses
    .filter(
      (x) =>
        !ignoreClasses.includes(x) &&
        measurementClasses?.some((mClass) => mClass.className === getBaselineUnitClass(x))
    )
    .map((x) => ({ label: intl.formatMessage({ id: x }), value: x }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const getLimits = (partKey: IPartKey) =>
    getBoundsForClass(getBoundsClass(partKey, className), unit);

  const isChanged = (partKey: IPartKey, field: IField) => {
    const initial = initialParts[partKey]?.[field] ?? '';
    const value = parts[partKey]?.[field] ?? '';
    return initial === '' || value === '' ? initial !== value : Number(initial) !== Number(value);
  };

  const getFieldError = (partKey: IPartKey, field: IField) => {
    const value = parts[partKey]?.[field] ?? '';
    if (value === '' || !isChanged(partKey, field) || isNaN(Number(value))) return undefined;
    const limits = getLimits(partKey);
    if (limits && (Number(value) < Number(limits.min) || Number(value) > Number(limits.max))) {
      return 'bounds';
    }
    return undefined;
  };

  const getOrderError = (partKey: IPartKey) => {
    const low = parts[partKey]?.low ?? '';
    const high = parts[partKey]?.high ?? '';
    if (low === '' || high === '') return false;
    return Number(high) < Number(low);
  };

  const partKeys = className ? getPartKeys(className) : [];
  const isComplete = partKeys.every((partKey) =>
    (['low', 'high'] as IField[]).every((field) => {
      const value = parts[partKey]?.[field] ?? '';
      return value !== '' && !isNaN(Number(value));
    })
  );
  const hasErrors = partKeys.some(
    (partKey) =>
      getOrderError(partKey) ||
      getFieldError(partKey, 'low') !== undefined ||
      getFieldError(partKey, 'high') !== undefined
  );
  const hasChanges =
    !baseline ||
    partKeys.some((partKey) => isChanged(partKey, 'low') || isChanged(partKey, 'high')) ||
    (mode === 'carePlan' && toDateString(effectiveDate) !== baseline.effectiveDate);
  const isSubmittable =
    !!className &&
    isComplete &&
    !hasErrors &&
    hasChanges &&
    (mode === 'template' || !!effectiveDate) &&
    !loading;

  const handleClass = (option: ISelectOption) => {
    const newUnit = getDefaultUnit(option.value);
    const emptyParts = getPartKeys(option.value).reduce<IPartInputs>((acc, partKey) => {
      acc[partKey] = { low: '', high: '' };
      return acc;
    }, {});
    setClassName(option.value);
    setUnit(newUnit);
    setInitialParts(emptyParts);
    setParts(emptyParts);
  };

  const handleValue = (partKey: IPartKey, field: IField, value: string) => {
    setParts((prevState) => ({
      ...prevState,
      [partKey]: { low: '', high: '', ...prevState[partKey], [field]: value }
    }));
  };

  const handleSubmit = () => {
    const toRange = (partKey: IPartKey) => ({
      low: Number(parts[partKey]?.low),
      high: Number(parts[partKey]?.high)
    });
    const values: IBaselineFormValues =
      className === PRESSURE_BASELINE_CLASS
        ? { class: className, unit, systolic: toRange('systolic'), diastolic: toRange('diastolic') }
        : { class: className, unit, ...toRange('main') };
    onSave(
      mode === 'carePlan' ? { ...values, effectiveDate: toDateString(effectiveDate) } : values
    );
  };

  const unitLabel = className ? getBaselineUnitLabel(className, unit, intl) : '';

  const renderInput = (partKey: IPartKey, field: IField) => {
    const fieldError = getFieldError(partKey, field);
    const orderError = field === 'high' && getOrderError(partKey);
    return (
      <Col xs={6}>
        <Form.Label className="baseline-modal-field-label">
          {intl.formatMessage({
            id: field === 'low' ? 'carePlanBaselineLowKey' : 'carePlanBaselineHighKey'
          })}
        </Form.Label>
        <div className="baseline-modal-input">
          <FormControl
            data-test={`careplan-baseline-${partKey}-${field}`}
            type="number"
            min={0}
            value={parts[partKey]?.[field] ?? ''}
            isInvalid={!!fieldError || orderError}
            onChange={(evt: ChangeEvent<HTMLInputElement>) =>
              handleValue(partKey, field, evt.target.value)
            }
          />
          <span className="baseline-modal-unit">{unitLabel}</span>
          <Form.Control.Feedback type="invalid">
            {fieldError &&
              intl.formatMessage(
                { id: 'carePlanBaselineBoundsKey' },
                {
                  min: getLimits(partKey)?.min,
                  max: getLimits(partKey)?.max,
                  unit: unitLabel
                }
              )}
            {!fieldError && orderError && intl.formatMessage({ id: 'carePlanBaselineOrderKey' })}
          </Form.Control.Feedback>
        </div>
      </Col>
    );
  };

  return (
    <ModalV2
      data-test="careplan-baseline-modal"
      className="baseline-modal"
      show={show}
      onHide={() => setModal(false)}
      backdrop="static"
    >
      <ModalV2.Header closeButton className="align-items-center">
        <ModalV2.Title>
          {intl.formatMessage({
            id: baseline ? 'carePlanBaselineEditKey' : 'carePlanBaselineSetKey'
          })}
        </ModalV2.Title>
      </ModalV2.Header>
      <ModalV2.Body>
        <Form.Group className="mb-3">
          <Form.Label className="baseline-modal-label">
            {intl.formatMessage({ id: 'carePlanBaselineVitalKey' })}
          </Form.Label>
          {baseline ? (
            <div data-test="careplan-baseline-class" data-test-value={className}>
              {className && intl.formatMessage({ id: className })}
            </div>
          ) : (
            <Dropdown
              dataTestId="careplan-baseline-class-select"
              options={classOptions}
              variant="form"
              menuPosition="fixed"
              value={classOptions.find((option) => option.value === className) || null}
              onChange={handleClass}
            />
          )}
        </Form.Group>
        {partKeys.map((partKey) => (
          <Form.Group
            key={partKey}
            className={partKey === 'main' ? 'mb-3' : 'baseline-modal-part mb-3'}
            data-test={`careplan-baseline-${partKey}`}
          >
            {partKey !== 'main' && (
              <div className="baseline-modal-label">{intl.formatMessage({ id: partKey })}</div>
            )}
            <Row>
              {renderInput(partKey, 'low')}
              {renderInput(partKey, 'high')}
            </Row>
          </Form.Group>
        ))}
        {mode === 'carePlan' && (
          <Form.Group className="mb-3">
            <Form.Label className="baseline-modal-label d-block">
              {intl.formatMessage({ id: 'carePlanBaselineEffectiveDateKey' })}
            </Form.Label>
            <Row>
              <Col xs={6}>
                <DatePicker
                  dataTestId="careplan-baseline-effective-date"
                  selected={effectiveDate}
                  maxDate={maxDate}
                  popperProps={{ strategy: 'fixed' }}
                  onChange={(date: Date | null) => setEffectiveDate(date)}
                />
              </Col>
            </Row>
          </Form.Group>
        )}
        <Alert variant="info" className="baseline-modal-info d-flex align-items-start mb-0">
          <InfoIcon className="icon me-2 flex-shrink-0" />
          <span>{intl.formatMessage({ id: 'carePlanBaselineInfoKey' })}</span>
        </Alert>
        {loading && <Loading className="mt-4" />}
        {error && <Error className="mt-4" dismissible={false} />}
      </ModalV2.Body>
      <ModalV2.Footer>
        <Button
          data-test="careplan-baseline-modal-close-btn"
          variant="outline-secondary"
          className="baseline-modal-cancel"
          onClick={() => setModal(false)}
        >
          {intl.formatMessage({ id: 'Cancel' })}
        </Button>
        <Button
          data-test="careplan-baseline-modal-submit-btn"
          variant="primary"
          className="baseline-modal-save"
          disabled={!isSubmittable}
          onClick={handleSubmit}
        >
          {intl.formatMessage({ id: 'saveButtonTitle' })}
        </Button>
      </ModalV2.Footer>
    </ModalV2>
  );
};

export default AddEditBaseline;
